<template>
  <div
    class="controls"
    :class="{
      'value-is-empty': valueIsEmpty,
    }"
  >
    <div
      v-for="(option, index) in validOptions"
      :key="option.id"
      class="control"
    >
      <div
        v-if="option.enum"
        class="stk-input input-container is-select"
        :class="{
          'is-focused': focusedId === option.id,
          'is-required': isRequired,
          'is-empty': valueIsEmpty,
          'is-filled': !valueIsEmpty,
        }"
        @click="focusInput(index)"
      >
        <label v-if="displayTitle" class="field-label">
          <span class="field-title">{{ displayTitle }}</span>
          <CopyButton v-if="!isClientPortal" class="copy-button" :value="storedValue" />
          <span v-if="isRequired" class="required-label">Required</span>
        </label>
        <div class="stk-select">
          <select
            class="stk-reset select-input"
            ref="inputRefs"
            v-model="storedValue"
            :required="isRequired === true"
            @change="emitChange"
            @focus="focusedId = option.id"
            @blur="focusedId = null"
          >
            <option v-for="item in option.enum" :key="item" :value="item">
              {{ item }}
            </option>
          </select>
          <Icon class="select-icon" />
        </div>
      </div>

      <template
        v-else-if="
          option.type === 'string' ||
          option.type === 'integer' ||
          option.type === 'number'
        "
      >
        <div
          class="stk-input input-container is-string"
          :class="{
            'is-focused': focusedId === option.id,
            'is-required': isRequired,
            'is-empty': valueIsEmpty,
            'is-filled': !valueIsEmpty,
          }"
          @click="focusInput(index)"
        >
          <label v-if="displayTitle" class="field-label">
            <span class="field-title">{{ displayTitle }}</span>
            <CopyButton v-if="!isClientPortal" class="copy-button" :value="storedValue" />
            <span v-if="isRequired" class="required-label">Required</span>
          </label>
          <input
            ref="inputRefs"
            class="stk-reset dossier-input"
            :type="option.type === 'string' ? 'text' : 'number'"
            :required="isRequired === true"
            v-model="storedValue"
            @input="emitChange"
            @focus="focusedId = option.id"
            @blur="focusedId = null"
          />
        </div>
      </template>

      <template v-else-if="option.type === 'boolean'">
        <div class="stk-input input-container is-checkbox">
          <div v-if="displayTitle" class="field-label">
            <span class="field-title">{{ displayTitle }}</span>
            <CopyButton v-if="!isClientPortal" class="copy-button" :value="storedValue" />
          </div>
          <div class="checkbox-input">
            <input type="checkbox" v-model="storedValue" @change="emitChange" :required="isRequired === true" />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { getValue } from "@frontend/shared/Util.js";
import { CopyButton, Icon } from "@stakeui";
import { computed, nextTick, ref, watch, watchEffect} from "vue";
import { useDossierStore } from "../../../stores/dossierStore";
import { useClientPortalStore } from "../../../stores/clientPortalStore";

const dossierStore = useDossierStore();
const clientPortalStore = useClientPortalStore();

const emit = defineEmits(["input-changed"]);

const props = defineProps({
  options: Array,
  ids: Array,
  idsWithArrayIndices: {
    type: Array,
    default: () => [],
  },
  arrayIndex: {
    type: Number,
    default: 0,
  },
  displayTitle: {
    type: String,
    default: "",
  },
  variant: {
    type: String,
    default: '',
  },
});

const inputRefs = ref([]);
const focusedId = ref(null);
const copied = ref(false);
const inputValue = ref(
  getValue(dossierStore.currentValue, props.ids, props.arrayIndex)
);

const isClientPortal = computed(() => props.variant === 'client');
const isRequired = computed(() => {
  if (!isClientPortal) {
    return false;
  }

  const pathString = props.idsWithArrayIndices.join('.');
  const found = clientPortalStore.portalRequest?.fields.find(f => f.pathString === pathString);

  if (found && found.required) {
    return true;
  }

  return false;
});

watchEffect(() => {
  inputValue.value = getValue(
    dossierStore.currentValue,
    props.ids,
    props.arrayIndex
  );
});

const storedValue = computed({
  get() {
    return inputValue.value;
  },
  set(newValue) {
    inputValue.value = newValue;

    // Set edit mode when any field is changed
    dossierStore.setEditMode(true);

    let current = dossierStore.currentValue;
    const keys = [...props.ids];

    // Navigate to the correct position in the object
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];

      // If we're at an array, handle array index
      if (Array.isArray(current)) {
        if (current.length <= props.arrayIndex) {
          // Extend array if needed
          while (current.length <= props.arrayIndex) {
            current.push({});
          }
        }
        current = current[props.arrayIndex];
      }

      // Create intermediate object if it doesn't exist
      if (!current[key]) {
        current[key] = {};
      }

      current = current[key];
    }

    // Set the value at the final position
    const lastKey = keys[keys.length - 1];
    if (Array.isArray(current)) {
      if (current.length <= props.arrayIndex) {
        // Extend array if needed
        while (current.length <= props.arrayIndex) {
          current.push({});
        }
      }
      current[props.arrayIndex][lastKey] = newValue;
    } else {
      current[lastKey] = newValue;
    }

    // Force reactivity update
    dossierStore.setCurrentValue({ ...dossierStore.currentValue });
  },
});

const valueIsEmpty = computed(() => {
  return !storedValue.value || storedValue.value === null || typeof storedValue.value === 'undefined' || storedValue === '';
});

const validOptions = computed(() => {
  return props.options.filter((option) => option.type !== "null");
});

function emitChange() {
  emit("input-changed");
}

function focusInput(index) {
  nextTick(() => {
    inputRefs.value[index]?.focus();
  });
}

const handleCopy = async (textToCopy) => {
  try {
    await navigator.clipboard.writeText(textToCopy);
    copied.value = true;
    setTimeout(() => (copied.value = false), 1000);
  } catch (err) {
    console.error("Failed to copy:", err);
  }
};
</script>

<style scoped>
.controls,
.control {
  display: flex;
  flex-grow: 1;
  width: 100%;
}

.control:hover {
  .copy-button {
    opacity: 1;
  }
}

.dossier-input {
  width: 100%;
}

.clear-control {
  margin-left: 0.5rem;
}

.input-container {
  position: relative;
  padding: 0.5rem 0.75rem 0.75rem;
  flex-direction: column;
  gap: 0.25rem;
  width: 100%;
  align-items: stretch;

  &.is-string {
    cursor: text;
  }

  &:hover {
    border-color: var(--stk-control-hover-border-color);
    color: var(--stk-control-hover-color);
  }

  &.is-required.is-empty {
    background-color: hsl(var(--stk-danger-h) var(--stk-danger-s) 98%);
    border-color: var(--stk-danger-70);
    outline-color: var(--stk-danger-90);
    outline-style: var(--stk-control-focus-outline-style);
    outline-width: var(--stk-control-focus-outline-width);

    .required-label {
      color: var(--stk-danger-50);
    }
  }

  &.is-required.is-filled,
  &.is-focused {
    border-color: var(--stk-control-focus-border-color);
    outline-color: var(--stk-control-focus-outline-color);
    outline-style: var(--stk-control-focus-outline-style);
    outline-width: var(--stk-control-focus-outline-width);

    .required-label {
      color: var(--stk-primary-60);
    }
  }
}

.required-label {
  font-weight: 500;
}

.field-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--stk-text-weak);
  line-height: 1.25;
  z-index: 10;
  position: relative;
  text-align: start;
  margin-right: 0 !important;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.25rem;
}

.field-title {
  pointer-events: none;
}

.select-input {
  border: none;
  background-color: transparent;
  box-shadow: none;
  padding: 0;

  &:hover {
    background-color: transparent;
  }
}

.copy-button {
  opacity: 0;
}

.select-icon {
  right: 0;
}

.checkbox-input {
  display: flex;
  align-items: center;
  gap: 0.5em;
}
</style>
